/**
 * Competência e fluxo de caixa.
 *
 * As duas seções respondem à mesma pergunta por ângulos diferentes: a
 * competência diz quanto o mês PRODUZIU, o fluxo diz quanto o mês RECEBEU. Um
 * pacote faturado em setembro e parcelado em três vezes aparece inteiro na
 * competência e em três pedaços no caixa — e é essa diferença que explica o mês
 * que "deu lucro" sem a conta subir.
 */

import { Badge, Card, KpiCard, Progress } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert } from '@ds/feedback';
import { dataSimples, moeda } from '@/comuns/formato';
import type { LinhaDeCompetencia, LinhaDeFluxo } from '@/data/tipos';

/** "2026-09" vira "set/2026". */
export function mesCurto(mes: string): string {
    const [ano, m] = mes.split('-');
    if (!ano || !m) return mes;
    const nome = new Date(Date.UTC(Number(ano), Number(m) - 1, 1)).toLocaleDateString('pt-BR', { month: 'short', timeZone: 'UTC' });
    return `${nome.replace('.', '')}/${ano}`;
}

/** Valor com sinal explícito: "− R$ 1.200,00" não é o mesmo que "R$ 1.200,00". */
function comSinal(centavos: number): string {
    return `${centavos < 0 ? '− ' : '+ '}${moeda(Math.abs(centavos))}`;
}

function tomDoValor(centavos: number): string {
    if (centavos > 0) return 'var(--success-strong)';
    if (centavos < 0) return 'var(--danger-strong)';
    return 'var(--text-tertiary)';
}

export interface BarrasDoPeriodoProps {
    linhas: LinhaDeFluxo[];
    rotulo: (linha: LinhaDeFluxo) => string;
    descricao: string;
}

/**
 * O gráfico é ilustração; a tabela abaixo é o dado.
 *
 * Por isso ele sai da árvore de acessibilidade inteiro: barra sem número é
 * ruído para quem ouve a tela, e repetir os mesmos valores duas vezes obriga a
 * escutar o relatório em dobro.
 */
export function BarrasDoPeriodo({ linhas, rotulo, descricao }: BarrasDoPeriodoProps) {
    const maior = Math.max(1, ...linhas.map((l) => Math.abs(l.saldoCentavos)));
    return (
        <div role="img" aria-label={descricao} style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 120, paddingTop: 8 }}>
            {linhas.map((linha) => {
                const altura = Math.round((Math.abs(linha.saldoCentavos) / maior) * 92);
                const negativo = linha.saldoCentavos < 0;
                return (
                    <span key={linha.chave} aria-hidden="true" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, justifyContent: 'flex-end', height: '100%' }}>
                        <span
                            style={{
                                width: '100%', maxWidth: 34, height: Math.max(linha.saldoCentavos === 0 ? 2 : 4, altura),
                                borderRadius: 3,
                                background: linha.saldoCentavos === 0 ? 'var(--border-strong)' : negativo ? 'var(--danger)' : 'var(--burgundy-500)',
                            }}
                        />
                        <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                            {rotulo(linha)}
                        </span>
                    </span>
                );
            })}
        </div>
    );
}

export interface RelatorioDeCompetenciaProps {
    linhas: LinhaDeCompetencia[];
    regime: 'competencia' | 'caixa';
    /** Abre a composição do mês. */
    onSelecionar?: (linha: LinhaDeCompetencia) => void;
}

export function RelatorioDeCompetencia({ linhas, regime, onSelecionar }: RelatorioDeCompetenciaProps) {
    const colunas: DataTableColumn<LinhaDeCompetencia & { id: string }>[] = [
        { key: 'mes', header: 'Mês', sortable: true, render: (l) => <strong style={{ fontWeight: 600 }}>{mesCurto(l.mes)}</strong> },
        {
            key: 'receitaCentavos', header: regime === 'caixa' ? 'Entradas' : 'Receita', align: 'right',
            render: (l) => moeda(regime === 'caixa' ? l.caixaEntradasCentavos : l.receitaCentavos),
        },
        {
            key: 'despesaCentavos', header: regime === 'caixa' ? 'Saídas' : 'Despesa', align: 'right',
            render: (l) => moeda(regime === 'caixa' ? l.caixaSaidasCentavos : l.despesaCentavos),
        },
        {
            key: 'resultado', header: 'Resultado', align: 'right',
            render: (l) => {
                const valor = regime === 'caixa'
                    ? l.caixaEntradasCentavos - l.caixaSaidasCentavos
                    : l.receitaCentavos - l.despesaCentavos;
                return <strong style={{ fontWeight: 600, color: tomDoValor(valor) }}>{comSinal(valor)}</strong>;
            },
        },
        {
            key: 'diferenca', header: 'Diferença de caixa', align: 'right',
            /* O que foi faturado menos o que entrou. Sem esta coluna, competência
               e caixa viram dois relatórios que ninguém consegue conciliar. */
            render: (l) => {
                const diferenca = l.caixaEntradasCentavos - l.receitaCentavos;
                return <span style={{ color: tomDoValor(diferenca), fontVariantNumeric: 'tabular-nums' }}>{comSinal(diferenca)}</span>;
            },
        },
        {
            key: 'margem', header: 'Margem', align: 'right',
            render: (l) => {
                if (l.receitaCentavos === 0) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
                const margem = Math.round(((l.receitaCentavos - l.despesaCentavos) / l.receitaCentavos) * 100);
                return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
                        {/* Margem negativa não cabe numa barra de 0 a 100: a barra
                            zera e o número diz a verdade. */}
                        <Progress value={Math.max(0, Math.min(100, margem))} tone={margem < 0 ? 'danger' : 'brand'} style={{ flex: 1 }} size="sm" />
                        <span style={{ width: 42, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{margem}%</span>
                    </span>
                );
            },
        },
    ];

    const ultimo = linhas[linhas.length - 1];
    const diferenca = ultimo ? ultimo.caixaEntradasCentavos - ultimo.receitaCentavos : 0;

    return (
        <>
            <DataTable
                columns={colunas} rows={linhas.map((l) => ({ ...l, id: l.mes }))} rowKey="id"
                rowLabel={(l) => mesCurto(l.mes)}
                {...(onSelecionar ? { onRowClick: (l: LinhaDeCompetencia & { id: string }) => onSelecionar(l) } : {})}
                emptyMessage="Nenhum título no período."
            />
            {ultimo && diferenca !== 0 && (
                <Alert tone="info" title="Competência e caixa não fecham no mesmo número" dismissible={false}>
                    Em {mesCurto(ultimo.mes)} a diferença de {comSinal(diferenca)} é o que foi faturado no mês e ainda não entrou —
                    ou o que entrou agora e pertence a um mês anterior.
                </Alert>
            )}
        </>
    );
}

export interface FluxoDeCaixaProps {
    linhas: LinhaDeFluxo[];
    periodo: 'dia' | 'mes';
    /** Mês de referência, `YYYY-MM`, usado no rótulo do diário. */
    mes: string;
    /** Abre a composição do dia ou do mês. */
    onSelecionar?: (linha: LinhaDeFluxo) => void;
}

export function FluxoDeCaixa({ linhas, periodo, mes, onSelecionar }: FluxoDeCaixaProps) {
    const diario = periodo === 'dia';
    const entradas = linhas.reduce((s, l) => s + l.entradasCentavos, 0);
    const saidas = linhas.reduce((s, l) => s + l.saidasCentavos, 0);
    /* No diário o saldo é acumulado, então o resultado do período é a última
       linha; no mensal cada linha já é o resultado do próprio mês. */
    const resultado = entradas - saidas;
    const comMovimento = linhas.filter((l) => l.entradasCentavos > 0 || l.saidasCentavos > 0);

    const colunas: DataTableColumn<LinhaDeFluxo & { id: string }>[] = [
        {
            key: 'chave', header: diario ? 'Dia' : 'Mês', sortable: true,
            render: (l) => <strong style={{ fontWeight: 600 }}>{diario ? dataSimples(l.chave) : mesCurto(l.chave)}</strong>,
        },
        {
            key: 'entradasCentavos', header: 'Entradas', align: 'right',
            render: (l) => (l.entradasCentavos ? <span style={{ color: 'var(--success-strong)', fontWeight: 500 }}>+ {moeda(l.entradasCentavos)}</span> : <span style={{ color: 'var(--text-tertiary)' }}>—</span>),
        },
        {
            key: 'saidasCentavos', header: 'Saídas', align: 'right',
            render: (l) => (l.saidasCentavos ? <span style={{ color: 'var(--danger-strong)', fontWeight: 500 }}>− {moeda(l.saidasCentavos)}</span> : <span style={{ color: 'var(--text-tertiary)' }}>—</span>),
        },
        {
            key: 'saldoCentavos', header: diario ? 'Saldo acumulado' : 'Resultado', align: 'right',
            render: (l) => <strong style={{ fontWeight: 600, color: tomDoValor(l.saldoCentavos) }}>{comSinal(l.saldoCentavos)}</strong>,
        },
    ];

    return (
        <>
            <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                <KpiCard label="Entradas" value={moeda(entradas)} hint="líquido de taxa" />
                <KpiCard label="Saídas" value={moeda(saidas)} />
                <KpiCard label="Resultado do período" value={comSinal(resultado)} deltaTone={resultado >= 0 ? 'up' : 'down'} />
                <KpiCard label={diario ? 'Dias com movimento' : 'Meses com movimento'} value={String(comMovimento.length)} hint={`de ${linhas.length}`} />
            </div>

            <Card
                title={diario ? 'Saldo acumulado no mês' : 'Resultado por mês'}
                subtitle={diario ? 'Cada barra é o acumulado até aquele dia' : 'Últimos seis meses'}
                padding={18}
                actions={<Badge tone="neutral">{comMovimento.length === 0 ? 'sem movimento' : `${comMovimento.length} com movimento`}</Badge>}
            >
                <BarrasDoPeriodo
                    linhas={linhas}
                    rotulo={(l) => (diario ? l.chave.slice(-2) : mesCurto(l.chave).slice(0, 3))}
                    descricao={
                        diario
                            ? `Saldo acumulado dia a dia; ao fim do mês, ${comSinal(resultado)}.`
                            : `Resultado mês a mês nos últimos seis meses; no período, ${comSinal(resultado)}.`
                    }
                />
            </Card>

            {comMovimento.length === 0 && (
                <Alert tone="neutral" title="Nenhum pagamento no período" dismissible={false}>
                    {diario
                        ? `Nenhuma baixa registrada em ${mes}. O fluxo mostra o que entrou e saiu de fato — títulos em aberto aparecem em Contas a receber e a pagar.`
                        : 'Nenhuma baixa registrada nos últimos seis meses.'}
                </Alert>
            )}

            <DataTable
                columns={colunas}
                /* O diário mostra o mês inteiro, inclusive os dias parados: um
                   calendário com buracos esconde o dia em que não entrou nada. */
                rows={linhas.map((l) => ({ ...l, id: l.chave }))} rowKey="id"
                rowLabel={(l) => (diario ? dataSimples(l.chave) : mesCurto(l.chave))}
                {...(onSelecionar ? { onRowClick: (l: LinhaDeFluxo & { id: string }) => onSelecionar(l) } : {})}
                emptyMessage="Nenhum movimento no período."
            />
        </>
    );
}
